import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, FilmSlate, DownloadSimple, SpinnerGap } from '@phosphor-icons/react';
import { getSpineManager } from '../../lib/spine-manager';
import {
  exportAsGif, exportAsWebM, exportAsPngSequence, exportCurrentFramePng,
  downloadBlob, type ExportFormat, type ExportFps,
} from '../../lib/animation-exporter';
import { useSceneStore } from '../../stores/sceneStore';

interface ExportAnimDialogProps {
  open: boolean;
  onClose: () => void;
}

const FORMAT_META: Record<ExportFormat, { label: string; desc: string; needsTimeline: boolean }> = {
  gif:          { label: 'GIF',      desc: 'Universal, 256-color palette. Best for simple animations.', needsTimeline: true },
  webm:         { label: 'WebM',     desc: 'High quality video. Chrome/Firefox; Safari may vary.',       needsTimeline: true },
  'png-sequence': { label: 'PNG Seq', desc: 'Lossless frames as ZIP. Standard compositing handoff.',     needsTimeline: true },
  'png-frame':  { label: 'PNG Frame', desc: 'Single lossless PNG of the current frame.',                needsTimeline: false },
};

export default function ExportAnimDialog({ open, onClose }: ExportAnimDialogProps) {
  const sceneName = useSceneStore((s) => s.sceneName);

  const [format, setFormat] = useState<ExportFormat>('gif');
  const [fps, setFps] = useState<ExportFps>(24);
  const [duration, setDuration] = useState(3);
  const [progress, setProgress] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const needsTimeline = FORMAT_META[format].needsTimeline;

  // Auto-detect scene duration when dialog opens
  useEffect(() => {
    if (open) {
      const manager = getSpineManager();
      const d = manager.getSceneDuration();
      if (d > 0) setDuration(Math.min(Math.ceil(d * 10) / 10, 30));
      setError('');
      setProgress(0);
    }
  }, [open]);

  async function handleExport() {
    setError('');
    setProgress(0);
    setExporting(true);
    try {
      const safeName = sceneName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
      const options = { format, fps, duration };
      let blob: Blob;
      let filename: string;

      if (format === 'gif') {
        blob = await exportAsGif(options, setProgress);
        filename = `${safeName}_animation.gif`;
      } else if (format === 'webm') {
        blob = await exportAsWebM(options, setProgress);
        filename = `${safeName}_animation.webm`;
      } else if (format === 'png-sequence') {
        blob = await exportAsPngSequence(options, setProgress);
        filename = `${safeName}_frames.zip`;
      } else {
        blob = await exportCurrentFramePng();
        filename = `${safeName}_frame.png`;
      }

      downloadBlob(blob, filename);
      setProgress(1);
      setTimeout(() => { setExporting(false); onClose(); }, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
      setExporting(false);
    }
  }

  const totalFrames = needsTimeline ? Math.ceil(duration * fps) : 1;
  const { width, height } = (() => { try { return getSpineManager().getArtboardDimensions(); } catch { return { width: 1920, height: 1080 }; } })();
  const estimatedMB = format === 'gif'
    ? ((width * height * 4 * totalFrames) / 1_000_000).toFixed(0)
    : format === 'png-sequence'
    ? ((width * height * 4 * totalFrames) / 1_000_000).toFixed(0)
    : '~';

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o && !exporting) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/75 z-40" />
        <Dialog.Content
          data-testid="export-anim-dialog"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     w-[min(420px,calc(100vw-32px))]
                     bg-bg-elevated border border-border rounded-xl shadow-lg
                     focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-subtle">
            <div className="flex items-center gap-2">
              <FilmSlate size={16} className="text-accent" />
              <Dialog.Title className="font-serif text-md font-medium text-text-primary">
                Export Animation
              </Dialog.Title>
            </div>
            <Dialog.Close
              disabled={exporting}
              className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast disabled:opacity-40"
            >
              <X size={13} />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {/* Format */}
            <div>
              <label className="block text-xs font-mono font-semibold text-text-tertiary uppercase tracking-wide mb-2">
                Format
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {(Object.keys(FORMAT_META) as ExportFormat[]).map((f) => (
                  <button
                    key={f}
                    data-testid={`format-${f}`}
                    onClick={() => setFormat(f)}
                    disabled={exporting}
                    className={`py-2 rounded-md text-xs font-mono font-medium border transition-all duration-fast ${
                      format === f
                        ? 'bg-accent border-accent text-white'
                        : 'bg-bg-input border-border text-text-secondary hover:border-accent/50 hover:text-text-primary'
                    }`}
                  >
                    {FORMAT_META[f].label}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[10.5px] font-sans text-text-tertiary">
                {FORMAT_META[format].desc}
              </p>
              {format === 'png-sequence' && (
                <p className="mt-1 text-[10px] font-sans text-amber-400/80">
                  Large scenes may produce hundreds of MB. Consider shorter durations.
                </p>
              )}
            </div>

            {/* FPS + Duration — only for timeline-based formats */}
            {needsTimeline && (
              <>
                {/* FPS */}
                <div>
                  <label className="block text-xs font-mono font-semibold text-text-tertiary uppercase tracking-wide mb-2">
                    Frame Rate
                  </label>
                  <div className="flex gap-2">
                    {([12, 24, 30] as ExportFps[]).map((f) => (
                      <button
                        key={f}
                        data-testid={`fps-${f}`}
                        onClick={() => setFps(f)}
                        disabled={exporting}
                        className={`flex-1 py-1.5 rounded-md text-xs font-mono border transition-all duration-fast ${
                          fps === f
                            ? 'bg-accent border-accent text-white'
                            : 'bg-bg-input border-border text-text-secondary hover:border-accent/50 hover:text-text-primary'
                        }`}
                      >
                        {f} fps
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-mono font-semibold text-text-tertiary uppercase tracking-wide">
                      Duration
                    </label>
                    <span className="text-xs font-mono text-text-secondary">{duration.toFixed(1)}s</span>
                  </div>
                  <input
                    type="range"
                    min={0.5}
                    max={30}
                    step={0.5}
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    disabled={exporting}
                    data-testid="duration-slider"
                    className="w-full accent-accent"
                  />
                  <div className="flex justify-between text-[10px] font-mono text-text-tertiary mt-0.5">
                    <span>0.5s</span>
                    <span>30s</span>
                  </div>
                </div>
              </>
            )}

            {/* Info */}
            <div className="text-[10.5px] font-mono text-text-tertiary bg-bg-input rounded-md px-3 py-2 space-y-0.5">
              {needsTimeline && (
                <div className="flex justify-between">
                  <span>Frames</span>
                  <span>{totalFrames}</span>
                </div>
              )}
              {(format === 'gif' || format === 'png-sequence') && (
                <div className="flex justify-between">
                  <span>Est. uncompressed size</span>
                  <span>~{estimatedMB} MB</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Canvas</span>
                <span>{width} × {height}px</span>
              </div>
            </div>

            {/* Progress bar */}
            {exporting && (
              <div className="space-y-1.5">
                <div className="h-1.5 bg-bg-input rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all duration-200"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
                <p className="text-[10.5px] font-mono text-text-tertiary text-center">
                  {progress < 1 ? `${Math.round(progress * 100)}%…` : 'Saving…'}
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-xs font-sans text-error bg-error-muted rounded-md px-3 py-2">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 pb-4 flex gap-2 justify-end">
            <button
              onClick={onClose}
              disabled={exporting}
              className="px-3 py-1.5 text-xs font-sans font-medium text-text-secondary hover:text-text-primary bg-bg-input border border-border rounded-md hover:bg-bg-hover transition-all duration-fast disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              data-testid="export-anim-btn"
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-sans font-medium bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50 transition-all duration-fast"
            >
              {exporting
                ? <SpinnerGap size={12} className="animate-spin" />
                : <DownloadSimple size={12} />}
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
